import {
  BISHOP,
  COLOR_MASK,
  type Color,
  FLAG_ACTION,
  FLAG_CAPTURE,
  KING,
  KNIGHT,
  PAWN,
  QUEEN,
  ROOK,
  TYPE_MASK,
  WHITE,
  BLACK,
  colorBit,
  encodeMove,
  isLightSquare,
  moveFrom,
  movePromo,
  moveTo,
  parseSquare,
  pieceColor,
  squareName,
} from '../board.ts';
import { pieceDef } from '../pieces.ts';
import { FenError } from '../notation.ts';
import type { MoveContext } from '../rules.ts';
import type { Position, Variant } from '../types.ts';
import { STANDARD_FEN } from './classic.ts';
import { type SideMove, cellName, formatCells, parseCells, sideMovesFrom } from './sideboard.ts';

// Day 19 (draft). Captured pieces get a second chance (the gulag, as in Warzone). A captured
// piece (never a king) drops into the Pit, a small 8×4 board, on its usual starting square
// there: White's pieces along the bottom row with pawns in front, Black's along the top. After
// your move on the main board you make one move in the Pit too, or pass. Capture an enemy
// piece in the Pit and yours escapes back to the main board on one of its starting squares
// (you choose); the piece it captured is out for good. A pawn reaching the far side of the
// Pit escapes as a queen onto your second row. A piece landing on an occupied square, on
// either board, sends the occupant down (an enemy piece on the main board counts as
// captured; one already in the Pit is out of the game).
//
// The quick version: the full idea uses a whole second chessboard; an 8×4 Pit puts the two
// sides' pawns face to face so fights start at once.
//
// Pit moves are actions: from/to are Pit cells (row × 8 + file, row 0 at White's side),
// payload 0 for a move that stays in the Pit, 1 + n to escape to the n-th starting square
// in `escapeSquares`, PASS to pass.
//
// ext: [phase (0 main board, 1 Pit), en passant square saved during the Pit move, 32 Pit cells]

export const PIT_COLS = 8;
export const PIT_ROWS = 4;
export const PIT_PASS = 31;

const PHASE = 0;
const EP = 1;
const PIT = 2;

export const COMEBACK_FEN = `${STANDARD_FEN} m 8/8/8/8 -`;

export function pitCells(pos: { ext: Int32Array | null }): Int32Array {
  return pos.ext!.subarray(PIT, PIT + PIT_COLS * PIT_ROWS);
}

/** The side to move is making its Pit move. */
export function pitPhase(pos: { ext: Int32Array | null }): boolean {
  return pos.ext?.[PHASE] === 1;
}

function hasPitPieces(ext: Int32Array, c: Color): boolean {
  const bit = colorBit(c);
  for (let i = PIT; i < PIT + 32; i++) if (ext[i] & bit) return true;
  return false;
}

/** Where `c`'s piece of `type` may come back on the main board (no square holding a king). */
export function escapeSquares(board: Int8Array, c: Color, type: number, promoted: boolean): number[] {
  const back = c === WHITE ? 0 : 56;
  const pawns = c === WHITE ? 8 : 48;
  let files: number[];
  let row = back;
  if (type === PAWN || promoted) {
    files = [0, 1, 2, 3, 4, 5, 6, 7];
    row = pawns;
  } else if (type === KNIGHT) files = [1, 6];
  else if (type === BISHOP) files = [2, 5];
  else if (type === ROOK) files = [0, 7];
  else files = [3];
  return files.map((f) => row + f).filter((sq) => (board[sq] & TYPE_MASK) !== KING || !(board[sq] & COLOR_MASK));
}

/** The Pit cell a piece captured on `sq` drops into. */
export function pitCellFor(piece: number, sq: number): number {
  const c = pieceColor(piece) as Color;
  const t = piece & TYPE_MASK;
  const file = sq & 7;
  const row = c === WHITE ? (t === PAWN ? 1 : 0) : t === PAWN ? 2 : 3;
  let f = 3;
  if (t === PAWN) f = file;
  else if (t === KNIGHT) f = file < 4 ? 1 : 6;
  else if (t === ROOK) f = file < 4 ? 0 : 7;
  else if (t === BISHOP) f = isLightSquare(sq) === (c === WHITE) ? 5 : 2;
  return row * PIT_COLS + f;
}

function dropIntoPit(ctx: MoveContext, piece: number, sq: number) {
  const ext = ctx.next.ext!;
  const cell = pitCellFor(piece, sq);
  // Whoever was standing there is out of the game.
  ext[PIT + cell] = piece;
  ctx.fx('pit', sq);
}

function pitMoves(pos: Position, out: number[], capturesOnly: boolean) {
  const cells = pitCells(pos);
  const us = pos.turn;
  const bit = colorBit(us);
  const side: SideMove[] = [];
  for (let i = 0; i < cells.length; i++) if (cells[i] & bit) sideMovesFrom(cells, PIT_COLS, PIT_ROWS, i, us, side);
  for (const m of side) {
    const flags = FLAG_ACTION | (m.capture ? FLAG_CAPTURE : 0);
    if (m.capture || m.promotes) {
      const exits = escapeSquares(pos.board, us, cells[m.from] & TYPE_MASK, m.promotes);
      if (exits.length) {
        for (let k = 0; k < exits.length; k++) out.push(encodeMove(m.from, m.to, k + 1, flags));
        continue;
      }
    }
    if (!capturesOnly) out.push(encodeMove(m.from, m.to, 0, flags));
  }
  if (!capturesOnly) out.push(encodeMove(0, 0, PIT_PASS, FLAG_ACTION));
}

export const comeback: Variant = {
  id: 'comeback',
  info: {
    name: 'Comeback Chess',
    but: 'captured pieces fight their way back',
    tagline: 'Captured pieces drop into the Pit. Win a fight down there and you’re back on the board.',
    summary: 'Captured pieces drop into **the Pit**, a small board of their own. Capture down there and your piece **escapes** back to the main board.',
    rules: [
      'A captured piece (never a king) drops into the Pit, a small 8×4 board, on its usual starting square there.',
      'After your move on the main board, you make one move in the Pit too if you have pieces there (or pass).',
      'Capture an enemy piece in the Pit and yours escapes to one of its starting squares on the main board. The piece it took is out for good. A pawn crossing the Pit escapes as a queen onto your second row.',
      'Escaping onto an occupied square sends that piece to the Pit (an enemy counts as captured). Dropping onto an occupied Pit square knocks that piece out.',
      'Otherwise normal chess: checkmate wins; stalemate, repetition and the 50-move rule draw.',
    ],
    accent: '#b5e655',
    emoji: '⛓️',
    bots: ['Benchwarmer', 'Brawler', 'Underdog', 'Comeback Kid', 'Phoenix'],
  },
  startFen: COMEBACK_FEN,
  extSize: PIT + PIT_COLS * PIT_ROWS,
  // Anything in the Pit might still come back.
  insufficientMaterial: (pos) => !hasPitPieces(pos.ext!, WHITE) && !hasPitPieces(pos.ext!, BLACK),

  parseExtra(fields, pos) {
    const [phase = 'm', pit = '8/8/8/8', ep = '-'] = fields;
    if (phase !== 'm' && phase !== 'p') throw new FenError('Bad comeback phase');
    const ext = pos.ext!;
    ext[PHASE] = phase === 'p' ? 1 : 0;
    parseCells(pit, PIT_COLS, PIT_ROWS, ext, PIT, [PAWN, KNIGHT, BISHOP, ROOK, QUEEN]);
    ext[EP] = ep === '-' ? -1 : parseSquare(ep);
  },

  formatExtra(pos) {
    const ext = pos.ext!;
    return [ext[PHASE] ? 'p' : 'm', formatCells(pitCells(pos), PIT_COLS, PIT_ROWS), ext[EP] >= 0 ? squareName(ext[EP]) : '-'];
  },

  replaceMoves(pos, out, capturesOnly) {
    if (pos.ext![PHASE] !== 1) return false;
    pitMoves(pos, out, capturesOnly);
    return true;
  },

  afterMove(ctx: MoveContext) {
    const { next, prev, us, them, move } = ctx;
    const ext = next.ext!;
    if (ext[PHASE] === 0) {
      if (ctx.captured && (ctx.captured & TYPE_MASK) !== KING) dropIntoPit(ctx, ctx.captured, ctx.capturedSq);
      if (hasPitPieces(ext, us)) {
        ext[PHASE] = 1;
        ext[EP] = next.ep;
        next.turn = us;
      }
      return;
    }

    // The Pit move.
    const payload = movePromo(move);
    next.halfmove = prev.halfmove;
    if (payload !== PIT_PASS) {
      const from = moveFrom(move);
      const to = moveTo(move);
      const piece = ext[PIT + from];
      const victim = ext[PIT + to];
      ext[PIT + from] = 0;
      ext[PIT + to] = 0;
      if (victim) next.halfmove = 0;
      if (payload > 0) {
        const promoted = (piece & TYPE_MASK) === PAWN && Math.floor(to / PIT_COLS) === (us === WHITE ? PIT_ROWS - 1 : 0);
        const sq = escapeSquares(prev.board, us, piece & TYPE_MASK, promoted)[payload - 1];
        const back = promoted ? QUEEN | colorBit(us) : piece;
        const resident = next.board[sq];
        if (resident) {
          ctx.remove(sq, pieceColor(resident) === us ? 'displaced' : 'capture');
          dropIntoPit(ctx, resident, sq);
        }
        ctx.place(sq, back, 'escape');
        ctx.fx('escape', sq);
        next.halfmove = 0;
      } else ext[PIT + to] = piece;
    }
    ext[PHASE] = 0;
    next.ep = ext[EP];
    ext[EP] = -1;
    next.turn = them;
  },

  nextTurn(ctx) {
    return ctx.next.turn;
  },

  actionSan(pos, move) {
    const payload = movePromo(move);
    if (payload === PIT_PASS) return 'Pit: pass';
    const cells = pitCells(pos);
    const from = moveFrom(move);
    const to = moveTo(move);
    const piece = cells[from];
    const letter = pieceDef(piece & TYPE_MASK).letter.toUpperCase();
    let san = `Pit ${letter === 'P' ? '' : letter}${cellName(from, PIT_COLS)}${cells[to] ? 'x' : '–'}${cellName(to, PIT_COLS)}`;
    if (payload > 0) {
      const promoted = (piece & TYPE_MASK) === PAWN && Math.floor(to / PIT_COLS) === (pos.turn === WHITE ? PIT_ROWS - 1 : 0);
      san += ` ⇒ ${promoted ? 'Q' : letter === 'P' ? '' : letter}${squareName(escapeSquares(pos.board, pos.turn, piece & TYPE_MASK, promoted)[payload - 1])}`;
    }
    return san;
  },

  evaluate(pos) {
    // A piece in the Pit is worth a fraction of itself: it might come back.
    let score = 0;
    for (const p of pitCells(pos)) {
      if (!p) continue;
      const v = pieceDef(p & TYPE_MASK).value * 0.3;
      score += pieceColor(p) === WHITE ? v : -v;
    }
    return score;
  },
};
